import firebase from 'firebase/app';
import 'firebase/storage';

class ShortUrlService {
  // Firebase Storage base URL (common for all files)
  static STORAGE_BASE_URL = 'https://firebasestorage.googleapis.com/v0/b/';
  
  // Your Firebase project ID (you'll need to set this)
  static PROJECT_ID = null; // Will be set dynamically
  
  /**
   * Initialize the service with your Firebase project ID
   */
  static init(projectId) {
    this.PROJECT_ID = projectId;

  }

  /**
   * Convert long Firebase Storage URL to short format
   * @param {string} fullUrl - Full Firebase Storage URL
   * @returns {string} - Short URL format
   */
  static toShortUrl(fullUrl) {
    if (!fullUrl || !fullUrl.includes('firebase')) {
      return fullUrl; // Return as-is if not a Firebase URL
    }

    try {
      const url = new URL(fullUrl);
      
      // Extract the file path from the URL
      // Example: /o/question-images%2Fq-123%2F1703123456789_abc123.webp
      const pathMatch = url.pathname.match(/\/o\/(.+)/);
      if (!pathMatch) {
        return fullUrl; // Return as-is if can't parse
      }

      // Decode the path and extract the unique part
      const decodedPath = decodeURIComponent(pathMatch[1]);
      
      // Extract the unique filename (everything after image-questions/)
      const uniquePart = decodedPath.replace('image-questions/', '');
      
      return uniquePart; // Return just the unique part
    } catch (error) {
      console.error('Error converting to short URL:', error);
      return fullUrl; // Return as-is if error
    }
  }

  /**
   * Convert short URL back to full Firebase Storage URL
   * @param {string} shortUrl - Short URL format
   * @returns {string} - Full Firebase Storage URL
   */
  static async toFullUrl(shortUrl) {
    if (!shortUrl || shortUrl.includes('firebase') || shortUrl.startsWith('data:')) {
      return shortUrl;
    }

    if (!this.PROJECT_ID) {
      console.warn('Project ID not set, returning short URL as-is');
      return shortUrl;
    }

    try {
      // Handle both old and new formats
      let path;
      
      // Check if it's the old format (without q- prefix)
      if (shortUrl.match(/^\d+\//)) {
        // Old format: "1755582888016/1756109312627_ibs31q2gsh.webp"
        // Convert to new format: "q-1755582888016/1756109312627_ibs31q2gsh.webp"
        const parts = shortUrl.split('/');
        const questionId = parts[0];
        const filename = parts[1];
        path = `image-questions/q-${questionId}/${filename}`;
      } else if (shortUrl.startsWith('q-')) {
        // New format: "q-1755589702736/1756111570617_hstez29vwgp.webp"
        path = `image-questions/${shortUrl}`;
      } else {
        // Unknown format, try as-is
        path = `image-questions/${shortUrl}`;
      }
      
      // Get authenticated URL with token
      try {
        const storageRef = firebase.storage().ref(path);
        const authenticatedUrl = await storageRef.getDownloadURL();
        return authenticatedUrl;
      } catch (authError) {
        // Fallback to non-authenticated URL
        const encodedPath = encodeURIComponent(path);
        const fallbackUrl = `${this.STORAGE_BASE_URL}${this.PROJECT_ID}.appspot.com/o/${encodedPath}?alt=media`;
        return fallbackUrl;
      }
    } catch (error) {
      console.error('Error converting to full URL:', error);
      return shortUrl; // Return as-is if error
    }
  }

  /**
   * Upload image and return short URL
   * @param {File} file - Image file to upload
   * @param {string} questionId - Question ID
   * @returns {Promise<Object>} - Upload result with short URL
   */
  static async uploadImageWithShortUrl(file, questionId) {
    try {
      // Import the AdvancedImageOptimizer dynamically to avoid circular dependencies
      const { default: AdvancedImageOptimizer } = await import('./AdvancedImageOptimizer');
      
      // Upload the image using the existing service
      const result = await AdvancedImageOptimizer.uploadOptimizedImage(file, questionId);
      
      // Convert the full URL to short format
      const shortUrl = this.toShortUrl(result.url);
      
      return {
        ...result,
        url: result.url, // Keep full URL for immediate use
        shortUrl: shortUrl // Add short URL for database storage
      };
    } catch (error) {
      console.error('Error uploading image with short URL:', error);
      throw error;
    }
  }

  /**
   * Get full URL for display (converts short URL back to full URL)
   * @param {string} url - Short URL or full URL
   * @returns {string} - Full URL for display
   */
  static getDisplayUrl(url) {
    if (!url) return '';
    
    // If it's already a full URL or base64, return as-is
    if (url.includes('firebase') || url.startsWith('data:') || url.startsWith('blob:')) {
      return url;
    }
    
    // Convert short URL to full URL
    return this.toFullUrl(url);
  }

  /**
   * Get authenticated download URL for Firebase Storage
   * @param {string} url - Short URL or full URL
   * @returns {Promise<string>} - Authenticated download URL
   */
  static async getAuthenticatedUrl(url) {
    if (!url) return '';
    
    // If it's not a Firebase URL, return as-is
    if (!url.includes('firebase') && !url.startsWith('q-')) {
      return url;
    }
    
    try {
      // Convert short URL to full URL if needed
      const fullUrl = this.getDisplayUrl(url);
      
      // If it's a Firebase Storage URL, get authenticated download URL
      if (fullUrl.includes('firebasestorage.googleapis.com')) {
        // Extract the file path from the URL
        const urlObj = new URL(fullUrl);
        const pathMatch = urlObj.pathname.match(/\/o\/(.+?)\?/);
        
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          const storageRef = firebase.storage().ref(filePath);
          const downloadURL = await storageRef.getDownloadURL();
          return downloadURL;
        }
      }
      
      return fullUrl;
    } catch (error) {
      console.error('Error getting authenticated URL:', error);
      // Fallback to the original URL
      return this.getDisplayUrl(url);
    }
  }

  /**
   * Get short URL for database storage
   * @param {string} url - Full URL or short URL
   * @returns {string} - Short URL for database
   */
  static getStorageUrl(url) {
    if (!url) return '';
    
    // If it's already a short URL or base64, return as-is
    if (!url.includes('firebase')) {
      return url;
    }
    
    // Convert full URL to short URL
    return this.toShortUrl(url);
  }

  /**
   * Extract project ID from Firebase config
   * @param {Object} firebaseConfig - Firebase configuration
   * @returns {string} - Project ID
   */
  static extractProjectId(firebaseConfig) {
    if (firebaseConfig && firebaseConfig.storageBucket) {
      // Extract project ID from storage bucket (e.g., "project-id.appspot.com")
      const projectId = firebaseConfig.storageBucket.split('.')[0];
      return projectId;
    }
    return null;
  }

  /**
   * Auto-initialize with Firebase config
   * @param {Object} firebaseConfig - Firebase configuration
   */
  static autoInit(firebaseConfig) {
    const projectId = this.extractProjectId(firebaseConfig);
    
    if (projectId) {
      this.init(projectId);
    } else {
      console.warn('Could not extract project ID from Firebase config');
    }
  }


}

export default ShortUrlService; 